<template>
  <v-form class="pt-5" ref="form" @submit.prevent="submitForm" lazy-validation>
    <v-row>
      <v-col>
        <v-file-input
          v-model="selectedFile"
          label="Click to choose a CSV file"
          accept=".csv"
          @change="handleFileUpload"
          outlined
          dense
          required
          hint="Input only a file that has the header row defined."
          persistent-hint
        ></v-file-input>
      </v-col>
      <v-col>
        <v-menu
          v-model="dateMenu"
          transition="scale-transition"
          offset-y
          min-width="auto"
        >
          <template v-slot:activator="{ on, attrs }">
            <v-text-field
              v-model="expires_at"
              label="Expire date (optional)"
              prepend-icon="mdi-calendar"
              readonly
              v-bind="attrs"
              v-on="on"
              dense
              outlined
            ></v-text-field>
          </template>
          <v-date-picker v-model="expires_at"></v-date-picker>
        </v-menu>
      </v-col>
    </v-row>
    <v-row>
      <v-col>
        <v-text-field
          label="Asset name"
          v-model="name"
          dense
          outlined
          required
          :rules="[required]"
        ></v-text-field>
      </v-col>
      <v-col>
        <v-text-field
          label="Description"
          v-model="description"
          dense
          outlined
          required
          :rules="[required]"
        ></v-text-field>
      </v-col>
    </v-row>

    <template v-for="(item, index) in columns">
      <div class="mb-2" v-bind:key="'span' + index">
        <b>{{ item.name }}</b>
      </div>
      <v-row v-bind:key="'row' + index">
        <v-col>
          <v-combobox
            :key="index"
            v-model="item.type"
            :label="`Select or type a custom type:`"
            :items="columnTypes"
            outlined
            dense
            :rules="[required]"
          ></v-combobox>
        </v-col>
        <v-col>
          <v-combobox
            :key="index"
            v-model="item.unit"
            :label="`Select or type a custom unit:`"
            :items="columnUnits"
            item-text="unit_code"
            item-value="unit_code"
            outlined
            dense
            :hint="unitHint"
            :rules="[required]"
            @keydown="handleKeydown"
            @change="handleChange"
          >
            <template v-slot:item="{ item }">
              <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                  <div v-bind="attrs" v-on="on">
                    {{ item.unit_code }}
                  </div>
                </template>
                <span>{{ item.unit_name }}</span>
              </v-tooltip>
            </template>
          </v-combobox>
        </v-col>
      </v-row>
    </template>
    <div class="text-center">
      <v-btn :disabled="disableSubmit" :loading="uploading" type="submit"
        >Submit</v-btn
      >
    </div>
  </v-form>
</template>

<script>
import axios from "axios";
import EventBus from "./EventBus";
export default {
  props: {
    selectedFunctionalUnits: Array,
  },
  mounted: function () {
    const dbData = JSON.parse(sessionStorage.getItem("dbData"));
    this.columnUnits = Object.entries(dbData.unit_name_lookup).map(
      ([key, value]) => {
        return {
          unit_code: key,
          unit_name: value,
        };
      }
    );
  },
  data() {
    return {
      unitHint: "",
      selectedFile: null,
      csvData: [],
      columns: [],
      columnTypes: [
        "String",
        "Integer + Number",
        "Date + Datetime",
        "Boolean",
        "Null + Missing Value",
      ],
      columnUnits: [],
      description: "",
      disableSubmit: true,
      dateMenu: false,
      expires_at: null,
      name: "",
      required: (value) => !!value || "This field is required",
      uploading: false,
    };
  },
  methods: {
    handleKeydown() {
      this.unitHint =
        "Custom units are not advised. Please consider the provided options.";
    },
    handleChange() {
      this.unitHint = "";
    },
    handleFileUpload() {
      if (this.selectedFile) {
        const reader = new FileReader();
        reader.onload = (event) => {
          const csvText = event.target.result;
          const lines = csvText.split("\r\n");
          const header = lines[0].split(",");
          this.columns = header.map((column) => ({
            name: column,
            type: null,
            unit: null,
          }));
          this.disableSubmit = false;
        };
        reader.readAsText(this.selectedFile);
      } else {
        this.columns = [];
        this.disableSubmit = true;
      }
    },
    createRequestBody: function () {
      const functional_unit_ids = this.selectedFunctionalUnits.map(
        (fUnit) => fUnit.id
      );
      const columns = this.columns.map((column) => {
        if (typeof column.unit === "object")
          column.unit = column.unit.unit_code;
        return column;
      });

      const { name, expires_at, description } = this;
      let JSON = { name, expires_at, functional_unit_ids };
      JSON.measurement_units = { description, columns };
      return JSON;
    },
    async submitForm() {
      if (!this.$refs.form.validate()) return;

      const requestBody = this.createRequestBody();
      const formData = new FormData();
      formData.append("file", this.selectedFile);
      formData.append(
        "req",
        new Blob([JSON.stringify(requestBody)], {
          type: "application/json",
        })
      );
      const token = localStorage.getItem("jwt");

      try {
        this.uploading = true;
        const response = await axios.post(
          "../epsmarc/device/asset/file-upload",
          //"http://172.28.43.190:8080/epsmarc/device/asset/file-upload",
          formData,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json;",
            },
          }
        );

        if (response.status === 200) {
          this.uploading = false;
          console.log("CSV file uploaded successfully.");
          this.columns = [];
          this.$refs.form.reset();
          this.disableSubmit = true;
          this.$emit("closeUploadDialog");
          EventBus.$emit("assetUploadSuccess");
        } else {
          console.error("Error uploading CSV file.");
        }
      } catch (error) {
        this.uploading = false;
        if (error.response) {
          if (error.status === 400) {
            this.$emit(
              "uploadAssetsFailed",
              `Something went wrong with the processing of the CSV file.`
            );
          } else {
            this.$emit("uploadAssetsFailed", error.response.data.message);
          }
        } else if (error.request) {
          this.$emit(
            "uploadAssetsFailed",
            "No response from the server. Please try again later."
          );
        } else {
          this.$emit("uploadAssetsFailed", `Error in setting up the request.`);
        }
        this.disableSubmit = false;
      }
    },
  },
};
</script>

<style></style>
